import { useCallback, useRef } from 'react';
import { toUserMessage } from '@platform/api-client';
import { ALLOWED_DOCUMENT_MIME_TYPES } from '@platform/media';
import { useBoardAttachmentUpload } from '../hooks/useBoardUpload.ts';
import type { AttachmentInput } from '../types/board.type.ts';
import { formatFileSize } from '../common/utils.ts';

interface AttachmentUploaderProps {
  /** 현재 첨부 목록 (업로드 완료된 것) */
  value: AttachmentInput[];
  onChange: (next: AttachmentInput[]) => void;
  onError?: (message: string) => void;
}

const ACCEPT = [
  ...ALLOWED_DOCUMENT_MIME_TYPES,
  'image/png',
  'image/jpeg',
  'image/webp',
].join(',');

/**
 * 게시글 첨부 파일 업로더.
 *
 * 본문 이미지(에디터 노드)와 분리된 "다운로드 목록".
 * 파일 선택 즉시 S3 업로드 → 완료된 메타(s3Key/fileName/size/mime)만 목록에 추가.
 * 저장 시 부모는 이 목록을 attachments 로 함께 전송하고, 서버가 글과 연결한다.
 */
export function AttachmentUploader({
  value,
  onChange,
  onError,
}: AttachmentUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading, progress } = useBoardAttachmentUpload();

  const handleFiles = useCallback(
    async (files: FileList) => {
      for (const file of Array.from(files)) {
        try {
          const { media, s3Key, mimeType } = await upload({ file });
          const next: AttachmentInput = {
            s3Key: media?.url ? s3Key : s3Key,
            fileName: file.name,
            fileSize: file.size,
            mimeType,
          };
          onChange([...value, next]);
        } catch (err) {
          onError?.(`${file.name}: ${toUserMessage(err, '업로드에 실패했습니다.')}`);
        }
      }
    },
    [upload, value, onChange, onError],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) void handleFiles(e.target.files);
      e.target.value = '';
    },
    [handleFiles],
  );

  const removeAt = useCallback(
    (idx: number) => onChange(value.filter((_, i) => i !== idx)),
    [value, onChange],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={isUploading}
          className="inline-flex items-center gap-2 rounded-lg border border-black/15 dark:border-white/15 px-4 py-2 text-sm font-semibold text-neutral-800 dark:text-neutral-200 transition-colors hover:border-neutral-950 disabled:opacity-50"
        >
          📎 파일 첨부
        </button>
        {isUploading && (
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            업로드 중… {progress}%
          </span>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={onInputChange}
        />
      </div>

      {value.length > 0 && (
        <ul className="divide-y divide-black/[0.06] dark:divide-white/10 rounded-lg border border-black/10 dark:border-white/10">
          {value.map((att, idx) => (
            <li
              key={`${att.s3Key}-${idx}`}
              className="flex items-center justify-between gap-3 px-4 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {att.fileName}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {formatFileSize(att.fileSize)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                aria-label={`${att.fileName} 삭제`}
                className="shrink-0 rounded p-1.5 text-neutral-400 dark:text-neutral-500 transition-colors hover:bg-red-50 hover:text-red-500"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
